import { type DependencyList, useCallback } from 'react';

import type { EqualFunction } from './types/equal-function.type';

import { useCompareMemoize } from './hooks/compare-memoize.hook';

/**
 * A hook that works like `useCallback`, but compares dependencies using a custom equality function.
 *
 * @param callback - The callback function to memoize.
 * @param dependencies - The dependencies array to compare.
 * @param equals - The equality function to use for comparing dependencies.
 * @returns A memoized version of the callback function.
 */
export const useCompareCallback = <TFunction extends (...args: unknown[]) => unknown>(
  callback: TFunction,
  dependencies: DependencyList,
  equals: EqualFunction<DependencyList>
): TFunction => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: the dependencies are memoized using useCompareMemoize
  return useCallback(callback, useCompareMemoize(dependencies, equals));
};
